import { useEffect, useState } from "react";
import { Check, X, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel, Pill } from "@/components/kit";
import { api, errMsg, gbp } from "@/lib/api";
import { FEATURE_LABELS } from "@/lib/seo";
import { SectionHead } from "./common";

export const IntervalToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-full border border-white/10 p-1" data-testid="interval-toggle">
    {[["month", "Monthly"], ["year", "Yearly"]].map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`interval-${k}`}
        className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${value === k ? "bg-[#CCFF00] text-[#0B0F17]" : "text-slate-400"}`}>{l}</button>
    ))}
  </div>
);

export const PlanFeatures = ({ features }) => (
  <ul className="space-y-2 text-sm">
    {Object.entries(FEATURE_LABELS).map(([k, l]) => (
      <li key={k} className={`flex items-center gap-2 ${features?.[k] ? "text-slate-200" : "text-slate-500 line-through"}`}>
        {features?.[k] ? <Check className="h-4 w-4 text-[#CCFF00]" /> : <X className="h-4 w-4" />} {l}
      </li>
    ))}
  </ul>
);

export default function Billing({ teamId, reloadTeam }) {
  const [b, setB] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(null);
  const load = () => api.get(`/teams/${teamId}/billing`).then((r) => { setB(r.data); if (r.data.plan_interval) setInterval(r.data.plan_interval); });
  useEffect(() => {
    load();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/teams/${teamId}/billing/checkout`, { plan_id: p.id, interval, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current billing period?")) return;
    try {
      await api.post(`/teams/${teamId}/billing/cancel`);
      toast.success("Subscription will end at the close of this billing period");
      load();
      reloadTeam();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!b) return null;
  return (
    <div className="space-y-6">
      <SectionHead title="Plan & billing" text="Choose the plan that fits your club. Billed in £ via Stripe.">
        <IntervalToggle value={interval} onChange={setInterval} />
      </SectionHead>
      <Panel className="flex flex-wrap items-center justify-between gap-4" data-testid="current-plan-panel">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Current plan</p>
          <p className="mt-1 font-display text-3xl font-black uppercase text-white" data-testid="current-plan-name">{b.plan?.name || "All features"}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {b.plan_status && <Pill tone={b.plan_status === "past_due" ? "amber" : "green"}>{b.plan_status === "comp" ? "complimentary" : b.plan_status.replace("_", " ")}</Pill>}
            {b.plan_interval && b.has_subscription && <Pill>{b.plan_interval === "year" ? "yearly" : "monthly"}</Pill>}
            {b.cancel_at_period_end && <Pill tone="red">ends this period</Pill>}
          </div>
        </div>
        {b.has_subscription && !b.cancel_at_period_end && <Button variant="outline" className="rounded-full border-rose-500/40 bg-transparent text-rose-300" onClick={cancel} data-testid="cancel-subscription-btn">Cancel subscription</Button>}
      </Panel>
      <div className="grid gap-5 lg:grid-cols-3">
        {b.plans.map((p) => {
          const price = interval === "month" ? p.price_monthly : p.price_yearly;
          const current = b.plan?.id === p.id;
          return (
            <Panel key={p.id} className={`flex flex-col ${p.highlight ? "border-[#CCFF00]/50" : ""}`} data-testid={`billing-plan-${p.name.toLowerCase()}`}>
              <div className="flex items-center justify-between"><p className="font-display text-2xl font-black uppercase text-white">{p.name}</p>{p.highlight && <Pill tone="volt">popular</Pill>}</div>
              <p className="mt-1 text-sm text-slate-400">{p.description}</p>
              <p className="mt-5 font-display text-5xl font-black text-white">{price > 0 ? gbp(price) : "Free"}<span className="ml-1 text-sm font-semibold text-slate-500">{price > 0 && (interval === "month" ? "/mo" : "/yr")}</span></p>
              <div className="my-6 flex-1"><PlanFeatures features={p.features} /></div>
              {current ? <Button disabled variant="outline" className="rounded-full bg-transparent">Current plan</Button>
                : price > 0 ? <Button disabled={busy === p.id || b.has_subscription} onClick={() => subscribe(p)} className="rounded-full font-bold" data-testid={`subscribe-${p.name.toLowerCase()}`}><CreditCard className="mr-1 h-4 w-4" />{busy === p.id ? "Opening Stripe…" : `Subscribe ${interval === "month" ? "monthly" : "yearly"}`}</Button>
                  : <p className="text-center text-xs text-slate-500">Applies automatically without a paid plan</p>}
            </Panel>
          );
        })}
      </div>
      {b.has_subscription && <p className="text-xs text-slate-500">To switch plans, cancel your current subscription first; you keep its features until the period ends.</p>}
    </div>
  );
}
